/* eslint-disable no-console */
/* eslint-disable no-console */
import PropTypes from 'prop-types';
import React, { useContext, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { ApolloClient, ApolloProvider, getApolloContext } from '@apollo/client';
import { useEventContext } from '@nextgen/frontend-features-events-logic';
import { useAppShellContext } from './AppShellContext';
import { handleErrors } from './session-context';
import useLogFeError from './error/useLogFeError';
import {
  getHasAuthError,
  getHasShipToCustomerError,
  getHasNegativePriceError,
  getUserRolesFromLocalStorage,
  clearPIIData,
  getHasSessionError,
  getHasCartError,
  isPublicRoute
} from './app-shell-utils';

// Rendered inside <ApolloProvider> so useMutation inside useLogFeError has client context.
const AppShellApolloErrorLogger = ({ logErrorRef }) => {
  const logError = useLogFeError(false);
  // eslint-disable-next-line no-param-reassign
  logErrorRef.current = logError;
  return null;
};

AppShellApolloErrorLogger.propTypes = {
  logErrorRef: PropTypes.shape({ current: PropTypes.shape({}) })
};

const AppShellApolloProvider = ({ children }) => {
  const { client } = useContext(getApolloContext());
  const config = useAppShellContext();
  const { authErrorRedirect } = config.config.redirects;
  const history = useHistory();
  const { createEvent } = useEventContext();
  const logErrorRef = useRef(null);
  const roles = getUserRolesFromLocalStorage(config?.config?.main?.jwtSecretKey);

  const handleGQLError = async e => {
    if (isPublicRoute()) {
      return;
    }

    const hasAuthError = getHasAuthError(e);
    const hasShipToCustomerError = getHasShipToCustomerError(e);
    const hasNegativePriceError = getHasNegativePriceError(e);
    const hasSessionError = getHasSessionError(e);
    const hasCartError = getHasCartError(e);
    createEvent({ eventName: 'error', error: clearPIIData(e) });

    const errorMessages = Array.isArray(e) ? e.map(err => err?.message).join('; ') : 'unknown';
    let errorType = 'GenericError';
    if (hasAuthError) errorType = 'AuthError';
    else if (hasCartError) errorType = 'CartError';
    else if (hasShipToCustomerError) errorType = 'ShipToCustomerError';
    else if (hasNegativePriceError) errorType = 'NegativePriceError';
    else if (hasSessionError) errorType = 'SessionError';
    logErrorRef.current?.logErrorToServer({
      message: `AppShellApolloProvider GQL Error - ${errorType}: ${errorMessages}`,
      stackTrace: undefined,
      component: 'AppShellApolloProvider',
      type: 'error',
      statusCode: hasAuthError ? 401 : 500
    });

    const isAnag = roles.includes('anaguser');
    const anagUrl = config?.config?.anag?.portalUrl;
    if (hasAuthError) {
    
      // eslint-disable-next-line no-underscore-dangle
      const auth = typeof window !== 'undefined' ? window.__AUTH_INSTANCE__ : null;
      if (auth?.tryRecoverBackendSession) {
        try {
          const recovered = await auth.tryRecoverBackendSession();
          if (recovered) {
            try {
              await config.client?.reFetchObservableQueries?.();
            } catch (refetchErr) {
              logErrorRef.current?.logErrorToServer({
                message: `Refetch queries failed after session recovery: ${refetchErr?.message || 'unknown'}`,
                stackTrace: refetchErr?.stack,
                component: 'AppShellApolloProvider',
                type: 'warn',
                statusCode: 500
              });
            }
            return;
          }
        } catch (recoveryErr) {
          logErrorRef.current?.logErrorToServer({
            message: `Backend session recovery failed: ${recoveryErr?.message || 'unknown'}`,
            stackTrace: recoveryErr?.stack,
            component: 'AppShellApolloProvider',
            type: 'error',
            statusCode: 500
          });
          // fall through to redirect
        }
      }

      if (isAnag && anagUrl) {
        window.location.href = anagUrl;
      } else {
        const redirectPath = authErrorRedirect || '/auth/landing';
        history.push(redirectPath);
      }
    } else if (hasCartError) {
      // Don't redirect for cart errors; let component handle with notification
      // eslint-disable-next-line no-console
      console.warn('Cart error detected; suppressing redirect to allow component-level handling', e);
      
    } else if (hasShipToCustomerError) {
      history.push('/content/error?type=accessDenied', { errors: e });
    } else if (hasNegativePriceError) {
      history.push('/content/error?type=pricingIssue', { errors: e });
    } else if (hasSessionError) {
      history.push('/content/error?type=sessionConflict', { errors: e });
    } else {
      history.push('/content/error', { errors: e });
    }
  };

  const handleNetworkError = () => {
    history.push('/content/error');
  };
  const errorLink = handleErrors(handleGQLError, handleNetworkError);

  const effectiveClient = useMemo(() => errorLink && config.client
      ? new ApolloClient({
        cache: config.client.cache,
        link: errorLink.concat(config.client.link)
      })
      : config.client, [config, errorLink]);

  if (client) {
    return <> {children}</>;
  }

  return (
    <ApolloProvider client={effectiveClient}>
      <AppShellApolloErrorLogger logErrorRef={logErrorRef} />
      {children}
    </ApolloProvider>
  );
};

AppShellApolloProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AppShellApolloProvider;
